import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { preloadGuestImage } from './guestImageCache';
import { LightboxImage } from './LightboxImage';

export interface LightboxPhoto {
  src: string;
  alt: string | null;
  /** Das kleine Bild, meist schon im Speicher — es steht da, bis das große geladen ist. */
  placeholder?: string;
}

interface Props {
  photos: LightboxPhoto[];
  index: number;
  onClose: () => void;
  onNavigate: (nextIndex: number) => void;
  closeLabel: string;
  prevLabel: string;
  nextLabel: string;
  /** Steht da, wenn das Bild nicht geladen werden konnte — mit einem Knopf für den neuen Versuch. */
  failedLabel: string;
  retryLabel: string;
  /** Guest-Bearer-Token: wenn gesetzt, wird das aktuelle Bild auth-geschuetzt geladen. */
  token?: string;
}

export function PhotoLightbox({
  photos,
  index,
  onClose,
  onNavigate,
  closeLabel,
  prevLabel,
  nextLabel,
  failedLabel,
  retryLabel,
  token,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const total = photos.length;
  const current = photos[index];

  // Die Nachbarn vorab: beim Wischen liegt das nächste Bild schon bereit, statt erst dann übers
  // Netz zu kommen — genau das war am Handy die Wartezeit je Bild.
  useEffect(() => {
    if (!token || total < 2) return;
    preloadGuestImage(photos[(index + 1) % total]?.src, token);
    preloadGuestImage(photos[(index - 1 + total) % total]?.src, token);
  }, [index, photos, token, total]);

  const go = useCallback(
    (delta: number) => onNavigate((index + delta + total) % total),
    [index, total, onNavigate],
  );

  useEffect(() => {
    // Am Fenster in der Einfangphase: das Vollbild liegt oft in einem Fenster (Extras, Anfahrt), und
    // dessen Escape-Wächter hört am Dokument. Hier zuerst geschlossen und angehalten, sonst ginge mit
    // dem Vollbild auch das Fenster zu — samt der Auswahl darin.
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Tab') {
        // Focus-Trap: nur die Buttons im Dialog sind fokussierbar.
        const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener('keydown', onKey, true);
    dialogRef.current?.querySelector<HTMLElement>('button')?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, [go, onClose]);

  const photo = current;
  if (!photo) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt ?? undefined}
      className="z-100 fixed inset-0 flex items-center justify-center bg-black/85 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <X className="size-5" aria-hidden />
      </button>
      {total > 1 && (
        <button
          type="button"
          aria-label={prevLabel}
          onClick={() => go(-1)}
          className="absolute left-4 flex size-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <ChevronLeft className="size-6" aria-hidden />
        </button>
      )}
      <LightboxImage
        key={photo.src}
        photo={photo}
        token={token}
        failedLabel={failedLabel}
        retryLabel={retryLabel}
      />
      {total > 1 && (
        <button
          type="button"
          aria-label={nextLabel}
          onClick={() => go(1)}
          className="absolute right-4 flex size-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <ChevronRight className="size-6" aria-hidden />
        </button>
      )}
      <span className="absolute bottom-4 rounded-full bg-white/10 px-3 py-1 font-mono text-xs text-white">
        {index + 1} / {total}
      </span>
    </div>
  );
}
